/* ----------------------------------------------------------------------------
   Every image on the public site, in one place.

   To use your own image:
     1. Put the file in  public/images/   e.g. public/images/home-hero.jpg
     2. Change that slot's value below to "/images/home-hero.jpg"
     3. Rebuild. Nothing else needs to change.

   Sizes are the smallest that will stay sharp on a large screen. Bigger is
   fine; export as JPG (photos) or WebP at roughly 80% quality, and keep each
   file under about 400 KB so pages stay fast.

   If a file is missing or misnamed, the page shows a branded panel in its
   place rather than a broken-image icon, so a typo can't break the layout.

   Case study and article images are NOT here: they are uploaded per project
   in the studio dashboard (/admin).
---------------------------------------------------------------------------- */
import teamUrl from "@/assets/team.webp"
import teamSofaUrl from "@/assets/team-sofa.webp"
import heroAbstractUrl from "@/assets/hero-abstract.webp"

const unsplash = (id: string, w: number, h: number) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&h=${h}&fit=crop&auto=format&q=80`

export const IMG = {
  /* ---- Homepage ---------------------------------------------------------- */

  /** Full-screen background behind the headline. Dark or moody works best:
      the text sits on top in white. 2400 × 1500. */
  heroAbstract: heroAbstractUrl,

  /** Service cards, "What we do". One per service. 1200 × 750. */
  serviceSoftware: unsplash("1537432376769-00f5c2f4c8d2", 1200, 750),
  serviceAutomation: unsplash("1518770660439-4636190af475", 1200, 750),
  serviceBrand: unsplash("1581291518857-4e27b48ff24e", 1200, 750),
  serviceSupport: unsplash("1551288049-bebda4e38f71", 1200, 750),

  /** Wide band between the services and "Why Sableboxx". Your team at work
      is ideal here. 2400 × 1030 (very wide). */
  homeBand: unsplash("1531482615713-2afd69097998", 2400, 1030),

  /** Portrait beside "Who we work with". 1200 × 1500. */
  team: teamUrl,

  /** Portrait beside the short "Who we are" block. 1200 × 1500. */
  homeAbout: teamSofaUrl,

  /* ---- About ------------------------------------------------------------- */

  /** Background behind the About page title. Uses `team` by default. */
  aboutHero: teamUrl,

  /** Portrait beside "Small on purpose, senior by design". 1200 × 1500. */
  teamSofa: teamSofaUrl,

  /** Wide band between "What we believe" and "How we work". 2400 × 1030. */
  aboutBand: unsplash("1552664730-d307ca884978", 2400, 1030),

  /* ---- Process ----------------------------------------------------------- */

  /** Background behind the Process page title. 2400 × 1200. */
  processHero: unsplash("1548248823-ce16a73b6d49", 2000, 1400),

  /** Wide band between the six stages and "What you can expect". 2400 × 1030. */
  processBand: unsplash("1519389950473-47ba0277781c", 2400, 1030),

  /* ---- Work, Insights, Contact ------------------------------------------- */

  /** Background behind the Work page title. 2400 × 1200. */
  workHero: unsplash("1583823388082-e66b8dfeb5ba", 2000, 1200),

  /** Background behind the Insights page title. 2400 × 1200. */
  insightsHero: unsplash("1629824019366-ffcb6b334634", 2000, 1200),

  /** Background behind the Contact page title and form. 2400 × 1400. */
  contactHero: teamSofaUrl,
}
